import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { ImagePlus, Loader2, X } from "lucide-react";
import { api, apiError, API } from "@/lib/api";
import { compressImage, MAX_PHOTO } from "@/lib/format";

const FIELDS = [
  ["founder_price", "Prix fondateur (€, TVA non applicable, art. 293 B du CGI, première année)", "number"],
  ["regular_price", "Prix normal (€, affiché barré)", "number"],
  ["seats", "Nombre de places fondateur", "number"],
  ["delivery_date", "Date de livraison (garantie de remboursement)", "text"],
  ["calendly_url", "Lien Calendly", "url"],
  ["contact_email", "Email de contact (pied de page)", "email"],
  ["notify_email", "Email qui reçoit les notifications (rappels, virements)", "email"],
  ["founder_payment_url", "Lien de paiement Stripe · offre fondateur (https://buy.stripe.com/…)", "url", true],
  ["member_payment_url", "Lien de paiement Stripe · tarif membre, affiché quand les places fondateur sont épuisées", "url", true],
  ["seats_sold_manual", "Ajustement manuel : places vendues hors de l'app (les paiements par lien Stripe et virement sont comptés automatiquement)", "number", true],
];
const LINK_HINT = "Collez l'adresse publique du lien (bouton « Copier » dans Stripe), pas l'identifiant plink_…";

function PhotoField({ path, onChange }) {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const upload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setBusy(true);
    try {
      const blob = await compressImage(file, 800);
      if (blob.size > MAX_PHOTO) throw new Error("La photo dépasse 5 Mo.");
      const form = new FormData();
      form.append("file", blob, "photo.jpg");
      const { data } = await api.post("/admin/founder-photo", form);
      onChange(data.founder_photo_path);
      toast.success("Photo mise à jour");
    } catch (err) { toast.error(err.response ? apiError(err) : err.message); } finally { setBusy(false); }
  };
  const remove = async () => { await api.delete("/admin/founder-photo"); onChange(null); toast.success("Photo retirée"); };
  return (
    <div className="sm:col-span-2">
      <span className="field-label">Photo de Hugo (page d'accueil)</span>
      <div className="flex items-center gap-4">
        {path ? <img src={`${API}/public/founder-photo?v=${encodeURIComponent(path)}`} alt="" className="w-20 h-20 rounded-full object-cover" data-testid="settings-founder-photo" />
          : <span className="grid place-items-center w-20 h-20 rounded-full bg-[#13201A] text-[#3DDC84] font-display font-bold text-2xl">HV</span>}
        <button type="button" className="btn-outline btn-sm" onClick={() => ref.current.click()} disabled={busy} data-testid="settings-founder-photo-upload">
          {busy ? <Loader2 size={15} className="animate-spin" /> : <ImagePlus size={15} />} {path ? "Changer" : "Ajouter une photo"}
        </button>
        {path && <button type="button" className="btn-ghost btn-sm" onClick={remove} data-testid="settings-founder-photo-remove"><X size={15} /> Retirer</button>}
        <input ref={ref} type="file" accept="image/*" className="hidden" onChange={upload} data-testid="settings-founder-photo-input" />
      </div>
    </div>
  );
}

function SiteSettings() {
  const [f, setF] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/admin/settings").then((r) => setF(r.data)).catch((e) => toast.error(apiError(e))); }, []);
  if (!f) return <p className="text-[#5B665F]">Chargement…</p>;
  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const body = Object.fromEntries(FIELDS.map(([k, , t]) => [k, t === "number" ? Number(f[k] || 0) : (f[k] ?? "").trim()]));
      const bad = ["founder_payment_url", "member_payment_url"].find((k) => body[k] && (!body[k].startsWith("https://buy.stripe.com/") || body[k].includes("/v1/")));
      if (bad) { toast.error("Lien Stripe invalide : collez l'adresse publique https://buy.stripe.com/… (bouton « Copier » dans Stripe)."); setBusy(false); return; }
      const { data } = await api.put("/admin/settings", body);
      setF(data);
      toast.success("Réglages enregistrés");
    } catch (err) { toast.error(apiError(err)); } finally { setBusy(false); }
  };
  return (
    <form onSubmit={save} className="card p-5 sm:p-7 grid grid-cols-1 sm:grid-cols-2 gap-5" data-testid="admin-site-settings">
      {FIELDS.map(([k, label, type, optional]) => (
        <label key={k} className={`block ${type === "url" || k === "notify_email" || k === "seats_sold_manual" ? "sm:col-span-2" : ""}`}>
          <span className="field-label">{label}</span>
          <input className="field" type={type} required={!optional} min={type === "number" ? (optional ? 0 : 1) : undefined} value={f[k] ?? ""} onChange={(e) => setF({ ...f, [k]: e.target.value })} data-testid={`settings-${k}-input`} />
          {k.endsWith("_payment_url") && <span className="block text-xs text-[#5B665F] mt-1">{LINK_HINT}</span>}
        </label>
      ))}
      <PhotoField path={f.founder_photo_path} onChange={(p) => setF({ ...f, founder_photo_path: p })} />
      <p className="sm:col-span-2 text-xs text-[#5B665F]">Modifier le prix fondateur met aussi à jour le prix du paiement Stripe.</p>
      <div className="sm:col-span-2"><button type="submit" className="btn-primary" disabled={busy} data-testid="settings-site-save">{busy ? "Enregistrement…" : "Enregistrer les réglages"}</button></div>
    </form>
  );
}

function ChangePassword() {
  const [f, setF] = useState({ current_password: "", new_password: "", confirm: "" });
  const [busy, setBusy] = useState(false);
  const save = async (e) => {
    e.preventDefault();
    if (f.new_password !== f.confirm) return toast.error("Les mots de passe ne correspondent pas");
    setBusy(true);
    try {
      await api.post("/auth/change-password", { current_password: f.current_password, new_password: f.new_password });
      setF({ current_password: "", new_password: "", confirm: "" });
      toast.success("Mot de passe modifié");
    } catch (err) { toast.error(apiError(err)); } finally { setBusy(false); }
  };
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  return (
    <form onSubmit={save} className="card p-5 sm:p-7 grid grid-cols-1 sm:grid-cols-3 gap-5" data-testid="admin-change-password">
      <label className="block"><span className="field-label">Mot de passe actuel</span><input className="field" type="password" required autoComplete="current-password" value={f.current_password} onChange={set("current_password")} data-testid="password-current-input" /></label>
      <label className="block"><span className="field-label">Nouveau (8 caractères min.)</span><input className="field" type="password" required minLength={8} autoComplete="new-password" value={f.new_password} onChange={set("new_password")} data-testid="password-new-input" /></label>
      <label className="block"><span className="field-label">Confirmer</span><input className="field" type="password" required minLength={8} autoComplete="new-password" value={f.confirm} onChange={set("confirm")} data-testid="password-confirm-input" /></label>
      <div className="sm:col-span-3"><button type="submit" className="btn-primary" disabled={busy} data-testid="password-save-button">Changer mon mot de passe</button></div>
    </form>
  );
}

export default function AdminSettings() {
  return (
    <section data-testid="admin-settings">
      <h1 className="text-3xl sm:text-4xl font-bold tracking-tight">Réglages</h1>
      <h2 className="text-lg font-bold mt-8 mb-3">Page d'accueil et notifications</h2>
      <SiteSettings />
      <h2 className="text-lg font-bold mt-10 mb-3">Mon compte</h2>
      <ChangePassword />
    </section>
  );
}
